import { radioStations, type RadioStation } from '@/mocks/radios';
import type { TechnicalStreamStatus } from '@/types/streaming';

// ─── Interfaz extendida para directorio global ───
export interface GlobalRadioStation {
  id: number;
  name: string;
  slug: string;
  frequency: string;
  city: string;
  state: string;
  country: string;
  category: string;
  language: string;
  listeners: number;
  rating: number;
  isLive: boolean;
  logoUrl: string;
  description: string;
  trendingScore: number;
  radioScore: number;
  followers: number;
  activityLevel: 'muy_activa' | 'activa' | 'moderada' | 'pausada';
  featured: boolean;
  technicalStreamStatus: TechnicalStreamStatus;
  streamAvailable: boolean;
}

// ─── Íconos por categoría ───
export const categoryIcons: Record<string, string> = {
  'Cristiana': 'ri-cross-line',
  'Regional Mexicana': 'ri-music-2-line',
  'Noticias': 'ri-newspaper-line',
  'Deportes': 'ri-football-line',
  'Pop': 'ri-music-line',
  'Rock': 'ri-flashlight-line',
  'Comunitaria': 'ri-group-line',
  'Internacional': 'ri-global-line',
  'Oficial': 'ri-verified-badge-line',
  'Jazz': 'ri-music-2-line',
  'Electrónica': 'ri-headphone-line',
  'Infantil': 'ri-emotion-happy-line',
  'Salsa': 'ri-music-line',
  'Clásica': 'ri-music-2-line',
  'Reggae': 'ri-music-line',
  'Hip Hop': 'ri-headphone-line',
  'Country': 'ri-music-2-line',
};

export const activityLabels: Record<string, string> = {
  muy_activa: 'Muy activa',
  activa: 'Activa',
  moderada: 'Moderada',
  pausada: 'En pausa',
};

export const activityColors: Record<string, string> = {
  muy_activa: 'bg-green-500',
  activa: 'bg-accent-500',
  moderada: 'bg-yellow-500',
  pausada: 'bg-foreground-300',
};

// ─── Metadatos adicionales por ID de RadioStation ───
// Solo IDs 1-12 tienen metadatos extendidos de directorio
const globalMeta: Record<number, Pick<GlobalRadioStation, 'trendingScore' | 'followers' | 'activityLevel' | 'featured'>> = {
  1:  { trendingScore: 92, followers: 12400, activityLevel: 'muy_activa', featured: true },
  2:  { trendingScore: 78, followers: 28900, activityLevel: 'activa',     featured: true },
  3:  { trendingScore: 98, followers: 56200, activityLevel: 'muy_activa', featured: true },
  4:  { trendingScore: 85, followers: 34100, activityLevel: 'moderada',   featured: false },
  5:  { trendingScore: 95, followers: 38900, activityLevel: 'muy_activa', featured: true },
  6:  { trendingScore: 62, followers: 8700,  activityLevel: 'activa',     featured: false },
  7:  { trendingScore: 97, followers: 47800, activityLevel: 'muy_activa', featured: true },
  8:  { trendingScore: 71, followers: 19300, activityLevel: 'moderada',   featured: false },
  9:  { trendingScore: 88, followers: 26700, activityLevel: 'muy_activa', featured: false },
  10: { trendingScore: 58, followers: 6200,  activityLevel: 'activa',     featured: false },
  11: { trendingScore: 82, followers: 21300, activityLevel: 'activa',     featured: false },
  12: { trendingScore: 91, followers: 45200, activityLevel: 'muy_activa', featured: true },
};

// ─── Construir directorio global desde la fuente única radios.ts ───
function buildGlobalRadio(station: RadioStation): GlobalRadioStation {
  const meta = globalMeta[station.id] || { trendingScore: 50, followers: Math.round(station.listeners * 3), activityLevel: 'moderada' as const, featured: false };
  return {
    id: station.id,
    name: station.name,
    slug: station.slug,
    frequency: station.frequency,
    city: station.city,
    state: station.state,
    country: station.country,
    category: station.category,
    language: station.language,
    listeners: station.listeners,
    rating: station.rating,
    isLive: station.isLive,
    logoUrl: station.logoUrl,
    description: station.description,
    radioScore: Math.round(station.radioScore),
    technicalStreamStatus: station.technicalStreamStatus,
    streamAvailable: station.streamAvailable,
    ...meta,
  };
}

export const globalRadioStations: GlobalRadioStation[] = radioStations
  .filter((s) => s.id >= 1 && s.id <= 12)
  .map(buildGlobalRadio);

// ─── Sort options ───
export type SortOption = 'trending' | 'most_listened' | 'most_active' | 'radio_score' | 'most_followers';

export const sortLabels: Record<SortOption, string> = {
  trending: 'Tendencia',
  most_listened: 'Más escuchadas',
  most_active: 'Más activas',
  radio_score: 'Mejor Radio Score',
  most_followers: 'Más seguidores',
};

export const sortIcons: Record<SortOption, string> = {
  trending: 'ri-fire-line',
  most_listened: 'ri-headphone-line',
  most_active: 'ri-flashlight-line',
  radio_score: 'ri-star-line',
  most_followers: 'ri-heart-line',
};

export function sortRadios(radios: GlobalRadioStation[], sort: SortOption): GlobalRadioStation[] {
  const sorted = [...radios];
  switch (sort) {
    case 'trending':
      sorted.sort((a, b) => b.trendingScore - a.trendingScore);
      break;
    case 'most_listened':
      sorted.sort((a, b) => b.listeners - a.listeners);
      break;
    case 'most_active': {
      const activityOrder: Record<string, number> = { muy_activa: 0, activa: 1, moderada: 2, pausada: 3 };
      sorted.sort((a, b) => activityOrder[a.activityLevel] - activityOrder[b.activityLevel]);
      break;
    }
    case 'radio_score':
      sorted.sort((a, b) => b.radioScore - a.radioScore);
      break;
    case 'most_followers':
      sorted.sort((a, b) => b.followers - a.followers);
      break;
    default:
      break;
  }
  return sorted;
}